import { Flex, Text } from '@radix-ui/themes';
import { ProjectorSetupDialog } from 'components/common/dialogs/projector-setup';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSwitchInput } from 'components/common/form/switch';
import { SettingButton } from 'components/common/settings-dialog/button';
import { SettingForm } from 'components/common/settings-dialog/form';
import { InactiveCallout } from 'components/common/settings-dialog/inactive-callout';
import { SettingRow } from 'components/common/settings-dialog/row';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { SfxName } from 'lib_ts/enums/machine-msg.enum';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { useContext, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';

const COMPONENT_NAME = 'MachineProjectorTab';

const ENABLE_TOGGLE_HITTER_DETAILS = false;

interface IProps {
  onSave: (payload: Partial<IMachine>) => void;
}

export const MachineProjectorTab = (props: IProps) => {
  const { loading, machine, isActive, playSound, requestOverlayIDs } =
    useContext(MachineContext);

  // ask for active overlays at mount
  useEffect(() => {
    requestOverlayIDs(COMPONENT_NAME);
  }, []);

  const [dialogProjector, setDialogProjector] = useState<number>();

  /** if the context is loading OR if you are not the active user on the machine */
  const disabled = loading || !isActive;

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <SettingForm>
        {!isActive && <InactiveCallout />}

        <SettingRow
          header="settings.projector-setup"
          description={<Text>{t('settings.projector-setup-msg')}</Text>}
          input={
            <SettingButton
              label="common.projector-setup"
              disabled={!isActive}
              onClick={() => setDialogProjector(Date.now())}
            />
          }
        />
        <SettingRow
          separatorBefore
          header="settings.projector-audio"
          description={<Text>{t('settings.projector-audio-msg')}</Text>}
          input={
            <SettingButton
              label="settings.play-chime"
              disabled={!isActive}
              onClick={() => playSound(SfxName.START)}
            />
          }
        />
        <SettingRow
          separatorBefore
          header="settings.preview-next-pitch"
          description={<Text>{t('settings.preview-next-pitch-msg')}</Text>}
          input={
            <Flex justify="end">
              <CommonSwitchInput
                id="projector-preview"
                disabled={disabled}
                defaultChecked={machine.enable_pitch_preview}
                onCheckedChange={(v) =>
                  props.onSave({
                    _id: machine._id,
                    enable_pitch_preview: v,
                  })
                }
              />
            </Flex>
          }
        />
        <SettingRow
          separatorBefore
          header="settings.recap-strike-zone"
          description={<Text>{t('settings.recap-strike-zone-msg')}</Text>}
          input={
            <Flex justify="end">
              <CommonSwitchInput
                id="projector-recap"
                disabled={disabled}
                defaultChecked={machine.enable_pitch_recap}
                onCheckedChange={(e) =>
                  props.onSave({
                    _id: machine._id,
                    enable_pitch_recap: e,
                  })
                }
              />
            </Flex>
          }
        />
        <SettingRow
          separatorBefore
          header="settings.recap-hit-data"
          description={
            <Text>
              <ReactMarkdown
                children={t('settings.recap-hit-data-msg').toString()}
              />
            </Text>
          }
          input={
            <Flex justify="end">
              <CommonSwitchInput
                id="projector-hit-stats"
                disabled={disabled}
                defaultChecked={machine.enable_hit_recap}
                onCheckedChange={(e) =>
                  props.onSave({
                    _id: machine._id,
                    enable_hit_recap: e,
                  })
                }
              />
            </Flex>
          }
        />

        {ENABLE_TOGGLE_HITTER_DETAILS && (
          <SettingRow
            separatorBefore
            header="settings.display-hitter-details"
            description={
              <Text>{t('settings.display-hitter-details-msg')}</Text>
            }
            input={
              <Flex justify="end">
                <CommonSwitchInput
                  id="projector-hitter-details"
                  disabled={disabled}
                  defaultChecked={machine.enable_hitter_details}
                  onCheckedChange={(e) =>
                    props.onSave({
                      _id: machine._id,
                      enable_hitter_details: e,
                    })
                  }
                />
              </Flex>
            }
          />
        )}
      </SettingForm>

      {dialogProjector && (
        <ProjectorSetupDialog
          key={dialogProjector}
          isActive={isActive}
          onClose={() => setDialogProjector(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
